import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Pillar, Element, Character } from '../types';
import { pillarsAPI, elementsAPI } from '../services/api';
import { initializeTabSwitching } from '../utils/tabSwitching';
import { useConfirmModal } from '../hooks/useConfirmModal';
import { useOptimizedImage } from '../hooks/useOptimizedImage';
import { useDynamicFontSize } from '../hooks/useDynamicFontSize';
import ConfirmModal from './ConfirmModal';
import UploadImageModal from './UploadImageModal';
import OptimizedImage from './OptimizedImage';
import LoadingOverlay from './LoadingOverlay';
import './PillarCard.css?v=2.0';
import { getImageSource, handleImageError } from '../utils/imageUtils';
import { useImageUpload } from '../hooks/useImageUpload';

// Extend Window interface for global functions
declare global {
  interface Window {
    showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning', duration?: number) => void;
  }
}

interface PillarCardProps {
  pillar: Pillar;
  onUpdate: (pillar: Pillar) => void;
  onDelete: (pillarId: string) => void;  
  onOpenPreviewPanel: (elementIds: string[]) => void;
  defaultTab?: string;
}

const PillarCard: React.FC<PillarCardProps> = ({
  pillar,
  onUpdate,
  onDelete,
  onOpenPreviewPanel,
  defaultTab = 'article'
}) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeleteBtn, setShowDeleteBtn] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageKey, setImageKey] = useState(0);
  const [sidePanelField, setSidePanelField] = useState(defaultTab);
  
  
  // Elements State
  const [referencedElements, setReferencedElements] = useState<Element[]>([]);
  const [elementsLoading, setElementsLoading] = useState(false);
  const [elementsError, setElementsError] = useState<string>('');
  const [hasLoadedElements, setHasLoadedElements] = useState(false);
  
  // Edit Parent Modal State
  const [showEditParentModal, setShowEditParentModal] = useState(false);
  const [allPillars, setAllPillars] = useState<Pillar[]>([]);
  const [selectedParentId, setSelectedParentId] = useState<string>('');
  const [isPopulatingDropdown, setIsPopulatingDropdown] = useState<boolean>(false);
  const [parentError, setParentError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pillarsLoaded, setPillarsLoaded] = useState<boolean>(false);
  
  // L1 Categories for organizing parent options - must match backend enum values
  const L1_CATEGORIES = [
    'Events and History',
    'Geography and Places', 
    'Organizations and Societies',
    'Culture and Beliefs',
    'Technology and Creations',
    'Flora and Fauna',
    'Magic and Abilities',
    'Races and Species'
  ];
  
  // Check if a pillar is an L1 pillar
  const isL1Pillar = (pillar: Pillar): boolean => {
    // An L1 pillar has its name matching one of the L1 categories
    return L1_CATEGORIES.slice(0, -1).includes(pillar.name); // Exclude 'Uncategorized'
  };
  
  // Reset hover state when edit parent modal opens
  useEffect(() => {
    if (showEditParentModal) {
      setShowDeleteBtn(false);
    }
  }, [showEditParentModal]);

  // Track if drag started within this component
  const [dragStartedHere, setDragStartedHere] = useState(false);

  // Global dragend listener to reset isDragging state when drag operation ends
  useEffect(() => {
    const handleGlobalDragEnd = () => {
      if (dragStartedHere) {
        setIsDragging(false);
        setDragStartedHere(false);
      }
    };

    document.addEventListener('dragend', handleGlobalDragEnd);
    
    return () => {
      document.removeEventListener('dragend', handleGlobalDragEnd);
    };
  }, [dragStartedHere]);


  
  // Card loading hook (now using useOptimizedImage with overlay)
  const imageSrc = getImageSource(pillar, 'pillar', imageKey);
  const {
    cardRef,
    overlayRef,
    loadingState = { isLoading: false, progress: 0, error: false }
  } = useOptimizedImage({
    src: imageSrc,
    alt: pillar.name,
    withCardOverlay: false
  });
  
  const { modalState, showConfirm, handleConfirm, handleCancel: handleModalCancel } = useConfirmModal();
  const navigate = useNavigate();

  // Dynamic font size for title
  const titleRef = useDynamicFontSize<HTMLHeadingElement>({
    minFontSize: 1.5,
    maxFontSize: 5,
    step: 0.1,
    containerPadding: 40,
    maxHeight: 80
  });

  // Initialize tab switching when component mounts
  useEffect(() => {
    initializeTabSwitching(pillar._id);
  }, [pillar._id]);

  // Update sidePanelField when defaultTab prop changes
  useEffect(() => {
    setSidePanelField(defaultTab);
  }, [defaultTab]);

  // Pre-fetch pillars for faster modal opening - only when modal is likely to be used
  useEffect(() => {
    const preloadPillars = async () => {
      if (!pillarsLoaded) {
        try {
          console.log('🔄 Preloading pillars for modal...');
          const response = await pillarsAPI.getAll();
          const pillars = Array.isArray(response) ? response : [];
          console.log('✅ Pillars preloaded:', pillars.length, 'pillars with slugs:', pillars.filter(p => p.slug).length);
          setAllPillars(pillars);
          setPillarsLoaded(true);
        } catch (error) {
          console.error('Failed to preload pillars:', error);
        }
      }
    };

    // Only preload if this is not an L1 pillar (since L1 pillars don't need parent editing)
    if (!isL1Pillar(pillar)) {
      preloadPillars();
    }
  }, [pillarsLoaded, pillar.name]);

  // Reset elements loading when pillar changes
  useEffect(() => {
    setHasLoadedElements(false);
    setReferencedElements([]);
    setElementsError('');
  }, [pillar._id]);

  // Load referenced elements when elements tab is selected - with debouncing
  useEffect(() => {
    const loadReferencedElements = async () => {
      if (sidePanelField === 'elements' && !hasLoadedElements && !elementsLoading) {
        setElementsLoading(true);
        setElementsError('');
        
        try {
          const elements = await elementsAPI.getByPillar(pillar._id);
          setReferencedElements(elements);
          setHasLoadedElements(true);
        } catch (error) {
          setElementsError('Failed to load elements');
          setHasLoadedElements(true);
        } finally {
          setElementsLoading(false);
        }
      }
    };

    // Add a small delay to prevent rapid tab switching from triggering multiple requests
    const timer = setTimeout(loadReferencedElements, 0); // Reduced from 50ms to 0ms for faster loading
    return () => clearTimeout(timer);
  }, [sidePanelField, pillar._id, hasLoadedElements, elementsLoading]);

  const handleEdit = (field: string, value: string) => {
    setEditingField(field);
    setEditValue(value);
  };

  const handleSave = async () => {
    if (!editingField) return;

    try {
      setLoading(true);
      
      const updateData = {
        [editingField]: editValue
      };
      
      const updatedPillar = await pillarsAPI.update(pillar._id, updateData);
      
      // If the backend didn't return an imageUrl, preserve the existing one
      const finalPillar = {
        ...updatedPillar,
        imageUrl: updatedPillar.imageUrl || pillar.imageUrl
      };
      
      onUpdate(finalPillar);
      setEditingField(null);
      
      // Show success toast
      if (typeof window.showToast === 'function') {
        window.showToast('Pillar updated successfully!', 'success');
      }
    } catch (error: any) {
      console.error('❌ Failed to update pillar:', error);
      
      // Show error toast
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to update pillar. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingField(null);
  };

  const handleDelete = async () => {
    // Check if this pillar has children by looking at the children array
    const hasChildren = pillar.children && pillar.children.length > 0;
    const childrenCount = pillar.children?.length || 0;
    
    // Create appropriate confirmation message
    let confirmMessage = `Are you sure you want to delete "${pillar.name}"?`;
    if (hasChildren) {
      confirmMessage += `\n\nThis will reassign ${childrenCount} child pillar${childrenCount > 1 ? 's' : ''} to the next parent in the hierarchy.`;
    }
    
    // Use React confirm modal
    const confirmDelete = await showConfirm(confirmMessage, { type: 'warning' });
    
    if (!confirmDelete) return;
    
    try {
      // Show processing message
      if (typeof window.showToast === 'function') {
        window.showToast(`Deleting "${pillar.name}" and reassigning children...`, 'info');
      }
      
      await pillarsAPI.delete(pillar._id);
      onDelete(pillar._id);
      
      // Show success toast with details
      if (typeof window.showToast === 'function') {
        if (hasChildren) {
          window.showToast(`"${pillar.name}" deleted successfully! ${childrenCount} child pillar${childrenCount > 1 ? 's' : ''} reassigned.`, 'success');
        } else {
          window.showToast(`"${pillar.name}" deleted successfully!`, 'success');
        }
      }
    } catch (error) {
      console.error('Failed to delete pillar:', error);
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to delete pillar.', 'error');
      }
    }
  };

  // Use shared image upload hook
  const { handleImageUpload, uploading: uploadState } = useImageUpload({
    entityId: pillar._id,
    cardType: 'pillars',
    entityName: pillar.name,
    onUpdate: (updatedData) => {
      const updatedPillar = { ...pillar, ...updatedData };
      onUpdate(updatedPillar);
    },
    setImageLoading,
    setImageKey,
    setUploading,
    showToast: typeof window.showToast === 'function' ? window.showToast : undefined
  });

  // Safety check - if pillar is undefined or null, don't render
  if (!pillar || !pillar._id) {
    console.warn('PillarCard: pillar prop is undefined or missing _id');
    return null;
  }

  const handleImageClick = () => {
    setDroppedFile(null);
    setShowUploadModal(true);
  };



  // Edit Parent Modal Functions
  const handleOpenEditParentModal = async () => {
    // Immediately disable hover state and open modal
    setShowDeleteBtn(false);
    setShowEditParentModal(true);
    setParentError('');
    
    // If pillars are already loaded, use them immediately
    if (pillarsLoaded && allPillars.length > 0) {
      setIsPopulatingDropdown(true);
      
      // Use setTimeout to allow modal to render first
      setTimeout(() => {
        // Filter out current pillar and its descendants to prevent circular references
        const filteredPillars = allPillars.filter((p: Pillar) => {
          return p._id !== pillar._id && !isDescendant(p._id, pillar._id, allPillars);
        });
        
        setAllPillars(filteredPillars);
        
        // Set initial selected parent
        if (typeof pillar.pillarParent === 'string') {
          setSelectedParentId(pillar.pillarParent);
        } else if (pillar.pillarParent && typeof pillar.pillarParent === 'object' && '_id' in pillar.pillarParent) {
          setSelectedParentId(pillar.pillarParent._id);
        } else {
          setSelectedParentId('');
        }
        
        setIsPopulatingDropdown(false);
      }, 0); // Minimal delay for smooth rendering
    } else {
      // If pillars aren't loaded yet, fetch them
      setIsPopulatingDropdown(true);
      
      try {
        const response = await pillarsAPI.getAll();
        const pillars = Array.isArray(response) ? response : [];
        
        // Filter out current pillar and its descendants
        const filteredPillars = pillars.filter((p: Pillar) => {
          return p._id !== pillar._id && !isDescendant(p._id, pillar._id, pillars);
        });
        
        setAllPillars(filteredPillars);
        setPillarsLoaded(true);
        
        // Set initial selected parent
        if (typeof pillar.pillarParent === 'string') {
          setSelectedParentId(pillar.pillarParent);
        } else if (pillar.pillarParent && typeof pillar.pillarParent === 'object' && '_id' in pillar.pillarParent) {
          setSelectedParentId(pillar.pillarParent._id);
        } else {
          setSelectedParentId('');
        }
        
      } catch (error) {
        setParentError('Failed to load pillars. Please try again.');
      } finally {
        setIsPopulatingDropdown(false);
      }
    }
  };

  const handleCloseEditParentModal = () => {
    setShowEditParentModal(false);
    setAllPillars([]);
    setSelectedParentId('');
    setParentError('');
    setSearchTerm('');
  };

  const handleUpdateParent = async () => {
    if (!selectedParentId && selectedParentId !== '') return;
    
    try {
      setLoading(true);
      
      const updateData: any = {};
      if (selectedParentId === '') {
        updateData.pillarParent = undefined; // No parent
      } else {
        updateData.pillarParent = selectedParentId;
      }
      
      const updatedPillar = await pillarsAPI.update(pillar._id, updateData);
      
      onUpdate(updatedPillar);
      handleCloseEditParentModal();
      
      if (typeof window.showToast === 'function') {
        const parentName = selectedParentId === '' ? 'no parent' : 
          allPillars.find(p => p._id === selectedParentId)?.name || 'selected parent';
        window.showToast(`Parent updated to "${parentName}"!`, 'success');
      }
      
    } catch (error) {
      console.error('Failed to update parent:', error);
      setParentError('Failed to update parent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Optimized helper function to check if a pillar is a descendant of another
  const isDescendant = (pillarId: string, ancestorId: string, allPillars: Pillar[]): boolean => {
    // Early exit if same ID
    if (pillarId === ancestorId) return false;
    
    const visited = new Set<string>();
    const maxDepth = 10; // Prevent infinite loops
    
    // Create a map for faster lookups
    const pillarMap = new Map(allPillars.map(p => [p._id, p]));
    
    const checkDescendant = (currentId: string, depth: number): boolean => {
      if (depth > maxDepth || visited.has(currentId)) {
        return false; // Prevent infinite loops
      }
      
      visited.add(currentId);
      const pillar = pillarMap.get(currentId);
      if (!pillar) return false;
      
      // Handle string parent ID
      if (typeof pillar.pillarParent === 'string') {
        if (pillar.pillarParent === ancestorId) return true;
        return checkDescendant(pillar.pillarParent, depth + 1);
      }
      
      // Handle object parent
      if (pillar.pillarParent && typeof pillar.pillarParent === 'object' && '_id' in pillar.pillarParent) {
        const parentObj = pillar.pillarParent as any;
        if (parentObj._id === ancestorId) return true;
        return checkDescendant(parentObj._id, depth + 1);
      }
      
      return false;
    };
    
    return checkDescendant(pillarId, 0);
  };

  // Organize pillars by L1 categories
  const organizePillarsByL1 = (pillars: Pillar[]) => {
    const groups: { [key: string]: Pillar[] } = {};
    
    // Safety check: ensure pillars is an array
    if (!Array.isArray(pillars)) {
      return groups;
    }
    
    pillars.forEach(pillar => {
      // Safety check: ensure pillar is valid
      if (!pillar || typeof pillar !== 'object' || !pillar.name) {
        return;
      }
      
      const l1Category = pillar.L1pillar || 'Uncategorized';
      if (!groups[l1Category]) {
        groups[l1Category] = [];
      }
      groups[l1Category].push(pillar);
    });
    
    // Sort pillars within each group by level first, then by name
    Object.keys(groups).forEach(category => {
      groups[category].sort((a, b) => {
        // Get levels for comparison
        const levelA = getPillarLevel(a);
        const levelB = getPillarLevel(b);
        
        // Extract numeric level for sorting (L1, L2, L3, etc.)
        const levelNumA = parseInt(levelA.replace('L', '')) || 1;
        const levelNumB = parseInt(levelB.replace('L', '')) || 1;
        
        // Sort by level first
        if (levelNumA !== levelNumB) {
          return levelNumA - levelNumB;
        }
        
        // If same level, sort by name
        return a.name.localeCompare(b.name);
      });
    });
    
    return groups;
  };

  // Filter pillars by search term
  const filterPillarsBySearch = (pillars: Pillar[]) => {
    // Safety check: ensure pillars is an array
    if (!Array.isArray(pillars)) {
      return [];
    }
    
    if (!searchTerm.trim()) return pillars;
    
    return pillars.filter(pillar => {
      // Safety check: ensure pillar is valid
      if (!pillar || typeof pillar !== 'object' || !pillar.name) {
        return false;
      }
      
      return pillar.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (pillar.L1pillar && pillar.L1pillar.toLowerCase().includes(searchTerm.toLowerCase()));
    });
  };

  // Get pillar level for display - use levelFromL1 property for safety
  const getPillarLevel = (pillar: Pillar): string => {
    // Safety check: ensure pillar is valid
    if (!pillar || typeof pillar !== 'object') {
      return 'L1';
    }
    
    // Use the levelFromL1 property if available (like CreatePillarModal does)
    if (pillar.levelFromL1) {
      return `L${pillar.levelFromL1}`;
    }
    
    // Fallback: try to determine level from parent
    if (!pillar.pillarParent) return 'L1';
    
    // If pillarParent is a string ID, find the parent in allPillars
    if (typeof pillar.pillarParent === 'string') {
      const parent = allPillars.find(p => p._id === pillar.pillarParent);
      if (parent && parent.levelFromL1) {
        return `L${parent.levelFromL1 + 1}`;
      }
      return 'L2'; // Default fallback
    }
    
    // If pillarParent is an object, use its levelFromL1
    if (pillar.pillarParent && typeof pillar.pillarParent === 'object' && '_id' in pillar.pillarParent) {
      const parentObj = pillar.pillarParent as any;
      if (parentObj.levelFromL1) {
        return `L${parentObj.levelFromL1 + 1}`;
      }
      return 'L2'; // Default fallback
    }
    
    return 'L2'; // Default fallback
  };

  // Drag and drop handlers for image upload
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragStartedHere(true);
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setDragStartedHere(false);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setDragStartedHere(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setDragStartedHere(false);
    
    const files = e.dataTransfer.files;
    if (files.length === 0) return;
    
    const file = files[0];
    
    if (!file.type.startsWith('image/')) {
      if (typeof window.showToast === 'function') {
        window.showToast('Please drop an image file.', 'error');
      }
      return;
    }
    
    // Set the dropped file and open upload modal
    setDroppedFile(file);
    setShowUploadModal(true);
  };



  const handleMouseEnter = () => {
    if (!showEditParentModal) {
      setShowDeleteBtn(true);
    }
  };

  const handleMouseLeave = () => {
    if (!showEditParentModal) {
      setShowDeleteBtn(false);
    }
  };

  return (
    <div 
      className={`pillar-card__container ${(showEditParentModal || modalState?.isOpen) ? 'modal-open' : ''}`}
      data-pillar-id={pillar._id}
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ position: 'relative' }}
    >
      {/* Card loading overlay */}
      <div ref={overlayRef} />
      {/* Delete button */}
      {showDeleteBtn && (
        <button 
          className="card__delete-btn" 
          onClick={handleDelete}
          title="Delete pillar"
        >
          ×
        </button>
      )}

      {/* Left Section: Pillar Image and L1 Pillar */}
      <div className="pillar-card__left">
        <div className="pillar-card__image-wrapper"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          style={{
            border: isDragging ? '3px dashed #55c57a' : '',
            backgroundColor: isDragging ? 'rgba(85, 197, 122, 0.1)' : ''
          }}
        >
                       <OptimizedImage
               src={getImageSource(pillar, 'pillar', imageKey)}
               alt={pillar.name}
               className="pillar-card__main-image"
               style={{
                 opacity: uploadState || imageLoading ? 0.5 : 1,
                 filter: uploadState || imageLoading ? 'grayscale(50%)' : 'none',
                 cursor: 'pointer',              
               }}
               onClick={handleImageClick}
               onLoad={() => setImageLoading(false)}
               onError={() => handleImageError(setImageLoading)}
               onDragOver={(e: React.DragEvent<HTMLImageElement>) => e.preventDefault()}
               onDragLeave={(e: React.DragEvent<HTMLImageElement>) => e.preventDefault()}
               onDrop={(e: React.DragEvent<HTMLImageElement>) => e.preventDefault()}
             />
          <LoadingOverlay isLoading={loadingState?.isLoading ?? false} />
          {/* Drag overlay */}
          <div 
            className="drag-overlay"
            style={{
              opacity: isDragging ? 1 : 0,
              borderColor: isDragging ? '#55c57a' : 'transparent'
            }}
          >
            <span style={{ color: '#55c57a', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Drop image here
            </span>
          </div>
        </div>
        
        {/* L1 Pillar Image - moved outside image wrapper */}
        {pillar.L1pillarImageUrl ? (
          <div className="pillar-card__l1-image-container">
            <OptimizedImage
              src={getImageSource({ imageUrl: pillar.L1pillarImageUrl }, 'pillar', imageKey)}
              alt={`L1 Pillar: ${pillar.L1pillar || 'Unknown'}`}
              className="pillar-card__l1-image"
              onLoad={() => setImageLoading(false)}
              onError={() => handleImageError(setImageLoading, false)}
              dataAttributes={{
                'data-image-key': pillar.L1pillarImage || '',
                'data-pillar-id': pillar._id
              }}
            />
            <div className="pillar-card__l1-name-overlay">
              <span>{pillar.L1pillar || 'Unknown L1'}</span>
            </div>
          </div>             
        ) : (
          <div className="pillar-card__l1-image-container">
            <span>No L1 Image</span>
          </div>
        )}
      </div>

             {/* Right Section: Tabs */}
       <div className="pillar-card__right">
         <h2 
           className="pillar-card__title" 
           ref={titleRef}
         >
           {pillar.name}
         </h2>
         <p className="pillar-card__level">Pillar Level {pillar.levelFromL1 || pillar.level || 1}</p>
         
         {/* Hierarchy button for L1 pillars */}
         {pillar.L1pillar === pillar.name && (
           <button 
             className="pillar-card__hierarchy-btn"
             onClick={() => navigate(`/pillars/hierarchy/${encodeURIComponent(pillar.name)}`)}
             title="View full hierarchy"
           >
             🌳 View Hierarchy
           </button>
         )}
         
         <div className="pillar-card__tabs">
           <button className={`pillar-card__tab-btn${sidePanelField === 'article' ? ' active' : ''}`} onClick={() => setSidePanelField('article')}>Article</button>
                       <button className={`pillar-card__tab-btn${sidePanelField === 'ancestors' ? ' active' : ''}`} onClick={() => setSidePanelField('ancestors')}>Ancestors</button>
           <button className={`pillar-card__tab-btn${sidePanelField === 'children' ? ' active' : ''}`} onClick={() => setSidePanelField('children')}>Children</button>
           <button className={`pillar-card__tab-btn${sidePanelField === 'elements' ? ' active' : ''}`} onClick={() => setSidePanelField('elements')}>Elements</button>
         </div>
                   <div className="pillar-card__tab-content">
            {sidePanelField === 'article' && (
              <div>
                <h4>Article</h4>
                <div 
                  className={`article-content ${editingField === 'article' ? 'editing' : ''} ${!pillar.article && editingField !== 'article' ? 'empty' : ''}`}
                  onClick={() => {
                    if (editingField !== 'article') {
                      handleEdit('article', pillar.article || '');
                      // Move cursor to end of text
                      setTimeout(() => {
                        const element = document.querySelector('.article-content') as HTMLElement;
                        if (element) {
                          const range = document.createRange();
                          const selection = window.getSelection();
                          range.selectNodeContents(element);
                          range.collapse(false);
                          selection?.removeAllRanges();
                          selection?.addRange(range);
                        }
                      }, 0);
                    }
                  }}
                  contentEditable={editingField === 'article'}
                  suppressContentEditableWarning={true}
                  onInput={(e) => {
                    if (editingField === 'article') {
                      const newValue = e.currentTarget.textContent || '';
                      setEditValue(newValue);
                    }
                  }}
                  onBlur={(e) => {
                    if (editingField === 'article') {
                      const newValue = e.currentTarget.textContent || '';
                      if (newValue !== pillar.article) {
                        setEditValue(newValue);
                        // Don't auto-save on blur, let user click save button
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    if (editingField === 'article') {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        const newValue = e.currentTarget.textContent || '';
                        if (newValue !== pillar.article) {
                          setEditValue(newValue);
                          handleSave();
                        } else {
                          handleCancel();
                        }
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        handleCancel();
                      }
                    }
                  }}                 
                >
                  {pillar.article || ''}
                </div>
                {editingField === 'article' && (
                  <div className="inline-edit-buttons" style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginTop: '0.5rem',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={handleSave}
                      disabled={loading}
                      style={{
                        background: loading ? '#ccc' : '#2196f3',
                        color: 'white',
                        border: 'none',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={loading}
                      style={{
                        background: loading ? '#ccc' : '#666',
                        color: 'white',
                        border: 'none',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
                                                          {sidePanelField === 'ancestors' && (
                    <div>
                      <div className="ancestors-header">
                        <h4>Ancestors</h4>
                        {!isL1Pillar(pillar) && (
                          <button 
                            className="edit-parent-btn-inline" 
                            onClick={handleOpenEditParentModal}
                            title="Edit pillar parent"
                          >
                            ⚙️
                          </button>
                        )}
                      </div>
                     <div className="chips-container">
                       {pillar.ancestors && pillar.ancestors.length > 0 ? (
                         pillar.ancestors.map((ancestor) => (
                           <div key={ancestor._id} className="chip">
                             <span className="chip-level">L{ancestor.level}</span>
                             <span className="chip-name">{ancestor.name}</span>
                           </div>
                         ))
                       ) : (
                         <div className="chip chip-empty">None</div>
                       )}
                     </div>
                   </div>
                 )}
                 {sidePanelField === 'children' && (
                   <div>
                     <h4>Children</h4>
                     <div className="chips-container">
                       {pillar.children && pillar.children.length > 0 ? (
                         pillar.children.map((child) => (
                           <div key={child._id} className="chip">
                             <span className="chip-level">L{child.level}</span>
                             <span className="chip-name">{child.name}</span>
                           </div>
                         ))
                       ) : (
                         <div className="chip chip-empty">None</div>
                       )}
                     </div>
                   </div>
                 )}
                           {sidePanelField === 'elements' && (
                   <div>
                     <h4>Elements</h4>
                     <div className="chips-container">
                       {elementsLoading ? (
                         <div className="chip chip-loading">Loading elements...</div>
                       ) : elementsError ? (
                         <div className="chip chip-error">{elementsError}</div>
                       ) : referencedElements.length > 0 ? (
                         referencedElements.map((element: Element) => (
                           <div key={element._id} className="chip">
                             <span className="chip-name">{element.name}</span>
                           </div>
                         ))
                       ) : (
                         <div className="chip chip-empty">No elements reference this pillar</div>
                       )}
                     </div>
                   </div>
                 )}
        </div>
        <div className="pillar-card__details-btn-wrapper">
          <Link 
            className="pillar-card__details-btn" 
            to={`/pillar/${pillar.slug}`}
            onClick={() => console.log('🔗 Details button clicked for pillar:', pillar.name, 'slug:', pillar.slug)}
          >
            Details
          </Link>
        </div>
      </div>

      {/* Confirm Modal - Rendered via Portal to avoid interference */}
      {modalState && createPortal(
        <ConfirmModal
          isOpen={modalState.isOpen}
          message={modalState.message}
          type={modalState.type}
          okText={modalState.okText}
          cancelText={modalState.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleModalCancel}
        />,
        document.body
      )}

      {/* Upload Image Modal - Rendered via Portal to avoid interference */}
      {showUploadModal && createPortal(
        <UploadImageModal
          isOpen={showUploadModal}
          characterName={pillar.name}
          initialFile={droppedFile || undefined}
          onUpload={handleImageUpload}
          onCancel={() => {
            setShowUploadModal(false);
            setDroppedFile(null);
          }}
        />,
        document.body
      )}

             {/* Edit Parent Modal - Rendered via Portal to avoid positioning issues */}
      {showEditParentModal && createPortal(
        <div 
          className="edit-parent-modal-overlay" 
          onClick={handleCloseEditParentModal}
          onMouseEnter={() => setShowDeleteBtn(false)}
          onMouseLeave={() => setShowDeleteBtn(false)}
        >
         <div className="edit-parent-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Pillar Parent</h3>
              <button 
                className="modal-close-btn"
                onClick={handleCloseEditParentModal}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="current-pillar-info">
                <h4>Current Pillar: {pillar.name}</h4>
                <p>Current Parent: {
                  typeof pillar.pillarParent === 'string' ? 
                    allPillars.find(p => p._id === pillar.pillarParent)?.name || 'Unknown' :
                  pillar.pillarParent && typeof pillar.pillarParent === 'object' && '_id' in pillar.pillarParent ?
                    pillar.pillarParent.name || 'Unknown' :
                    'No parent'
                }</p>
              </div>
              
              <div className="form-group">
                <label htmlFor="parent-select">Select New Parent</label>
                {(() => {
                  // Show loading message while populating
                  if (isPopulatingDropdown) {
                    return (
                      <div className="parent-selection-loading">
                        <LoadingOverlay 
                          isLoading={true} 
                          message={pillarsLoaded ? 'Processing...' : 'Loading pillars...'} 
                        />
                      </div>
                    );
                  }
                  
                  // Safety check: ensure allPillars is an array
                  if (!Array.isArray(allPillars)) {
                    return (
                      <div className="parent-selection-loading">
                        <LoadingOverlay isLoading={true} message="Loading..." />
                      </div>
                    );
                  }
                  
                  // Filter out the current pillar from the list
                  const availablePillars = allPillars.filter(p => p._id !== pillar._id);
                  const filteredPillars = filterPillarsBySearch(availablePillars);
                  const organizedPillars = organizePillarsByL1(filteredPillars);
                  
                  return (
                    <div className="parent-selection-container">
                      {L1_CATEGORIES.map(category => {
                        const categoryPillars = organizedPillars[category] || [];
                        if (categoryPillars.length === 0) return null;
                        
                        return (
                          <div key={category} className="parent-category-group">
                            <h5 className="category-title">{category}</h5>
                            <div className="pillar-boxes-container">
                              {categoryPillars.map(pillarOption => (
                                <div
                                  key={pillarOption._id}
                                  className={`pillar-box ${selectedParentId === pillarOption._id ? 'selected' : ''}`}
                                  onClick={() => setSelectedParentId(pillarOption._id)}
                                >
                                                                     <OptimizedImage
                                     src={getImageSource(pillarOption, 'pillar')}
                                     alt={pillarOption.name}
                                     className="pillar-box-background"
                                     style={{
                                       position: 'absolute',
                                       top: 0,
                                       left: 0,
                                       width: '100%',
                                       height: '100%',
                                       objectFit: 'cover',
                                       zIndex: 1
                                     }}
                                   />
                                   <div className="pillar-box-overlay" style={{ zIndex: 2 }}>
                                     <div className="pillar-box-content">
                                       <div className="pillar-box-level">
                                         {getPillarLevel(pillarOption)}
                                       </div>
                                       <div className="pillar-box-name">
                                         {pillarOption.name}
                                       </div>
                                     </div>
                                   </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }).filter(Boolean)}
                    </div>
                  );
                })()}
              </div>
              
              <div className="form-group">
                <label htmlFor="parent-search">Search Parents</label>
                <input
                  id="parent-search"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or L1 category..."
                />
              </div>
              
              {parentError && (
                <div className="error-message">
                  {parentError}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn--gray" 
                onClick={handleCloseEditParentModal}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className="btn btn--gold" 
                onClick={handleUpdateParent}
                disabled={loading || isPopulatingDropdown}
              >
                {loading ? 'Updating...' : 'Update Parent'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PillarCard; 